/**
 * Canonical rules for Microsoft Teams group-chat topics ("titles").
 *
 * This module is the single source of truth shared by the Convex backend (which
 * *derives* and stores the topic) and the Teams gateway (which *validates* the
 * topic at the transport boundary before it reaches Microsoft Graph). Keeping the
 * constants and the transport-safety predicate here means the two services can
 * never drift apart on what counts as a valid topic.
 *
 * Microsoft Graph reference:
 * - create chat (`POST /chats`): `topic` is optional and only allowed for group
 *   chats. There is no documented minimum length.
 * - update chat (`PATCH /chats/{id}`): topic max length is 250 characters and a
 *   colon (":") is not allowed.
 *
 * We keep a stricter *product* cap of 40 characters and strip ":" defensively so
 * a topic stays valid even if it is ever PATCHed later, and so we never depend on
 * a behaviour difference between the create and update endpoints.
 */
/** Product cap for a Teams chat topic, measured in Unicode code points. */
export const TEAMS_TOPIC_MAX_LENGTH = 40;
/**
 * Deterministic fallback used when no meaningful topic can be derived from the
 * source text. 24 characters: comfortably inside the product cap and readable.
 */
export const TEAMS_TOPIC_FALLBACK = "Clarification discussion";
/**
 * Cap for the optional "specific question" a requester can attach to a Teams
 * consultation. It is injected into Claudia's prompt context, so it is bounded to
 * keep prompt size predictable and stop an unbounded paste from crowding out the
 * clarification/thread context. Generous enough for a real question or two.
 */
export const TEAMS_SPECIFIC_QUESTION_MAX_LENGTH = 2000;
/**
 * A character is unsafe for a Teams topic when it is a control character
 * (C0 range or DEL) or a colon. Checked numerically to avoid embedding raw
 * control characters in source.
 */
function isUnsafeTopicChar(char) {
    const code = char.codePointAt(0) ?? 0;
    return code <= 0x1f || code === 0x7f || char === ":";
}
/**
 * Count Unicode code points rather than UTF-16 code units so an emoji or other
 * astral-plane character counts as one character (matching how a human and how
 * Graph perceive length) and never gets split mid-surrogate.
 */
function codePointLength(value) {
    return Array.from(value).length;
}
/**
 * Truncate to `max` code points, preferring a word boundary so we never emit a
 * half-word like "promo benchma". Falls back to a hard code-point cut for a
 * single very long word. Always surrogate-safe because we slice on code points.
 */
function truncateOnWordBoundary(value, max) {
    const chars = Array.from(value);
    if (chars.length <= max)
        return value;
    const hardSlice = chars.slice(0, max).join("").trimEnd();
    // If the character that *would* follow the cut is whitespace, we landed on a
    // clean boundary already and only need to tidy trailing punctuation.
    if (chars[max] === " ") {
        return hardSlice.replace(/[\s.,;!?-]+$/u, "").trim() || hardSlice;
    }
    const lastSpace = hardSlice.lastIndexOf(" ");
    const wordSlice = lastSpace > 0 ? hardSlice.slice(0, lastSpace) : hardSlice;
    return wordSlice.replace(/[\s.,;!?-]+$/u, "").trim() || hardSlice.trim();
}
/**
 * Normalize arbitrary source text into a transport-safe Teams topic:
 * strip unsafe characters, collapse whitespace, trim, and truncate to the
 * product cap on a word boundary. May return an empty string if the input was
 * entirely whitespace/unsafe characters — callers decide on a fallback.
 */
export function sanitizeTeamsTopic(raw) {
    let stripped = "";
    for (const char of raw) {
        stripped += isUnsafeTopicChar(char) ? " " : char;
    }
    const collapsed = stripped.replace(/\s+/g, " ").trim();
    return truncateOnWordBoundary(collapsed, TEAMS_TOPIC_MAX_LENGTH);
}
/**
 * Defensive boundary check used by the Teams gateway. A topic is transport-safe
 * when it is a non-empty string, already trimmed (no leading/trailing
 * whitespace), within the product cap (by code points), and free of control
 * characters and ":". Intentionally has **no** minimum length — a
 * short-but-meaningful title is valid.
 *
 * The check is performed on the RAW value, never a trimmed/normalized copy: the
 * gateway trusts this predicate as its boundary, so a value it would mangle (e.g.
 * a leading newline or trailing padding past the cap) must be rejected, not
 * silently repaired into something acceptable.
 */
export function isTransportSafeTeamsTopic(topic) {
    if (typeof topic !== "string")
        return false;
    if (topic.length === 0)
        return false;
    if (topic !== topic.trim())
        return false;
    if (codePointLength(topic) > TEAMS_TOPIC_MAX_LENGTH)
        return false;
    for (const char of topic) {
        if (isUnsafeTopicChar(char))
            return false;
    }
    return true;
}
